/**
 * `<caos-number-field>` — exact-decimal numeric input honoring the field precision
 * and scale, via `min` / `max` / `step`. (Catalog: Fields & input › NumberField — exact-decimal numeric input honoring the field precision and scale, via min / max / step.)
 *
 * Same states as TextField (label, placeholder, disabled, invalid + message, required,
 * focus ring); values render in tabular figures. It sets the `numeric` reflection so
 * the shared stylesheet applies tabular-nums. See `base-field.ts` for the template.
 *
 * CAOS-600 — A SLIDER IS A PRESENTATION OF THIS FIELD. `presentation="slider"` draws the same
 * number as a range control with its chosen value beside it. The incumbent catalogue ships a slider
 * as its own component with eighteen attributes, and almost every one of them — min, max, step,
 * label, disabled, validity, message — is this field's already. That overlap is the evidence it is
 * not a different kind of thing: the value is a number, constrained the way this field's numbers
 * are constrained, drawn along a line instead of in a box.
 *
 * THE SLIDER NEEDS A RANGE, AND SAYS SO BY NOT DRAWING. A range control with no declared `min` and
 * `max` silently spans the platform's own 0–100, which has nothing to do with the field — dragging
 * it to the far right would set 100 on a field whose values run to a million. So the presentation
 * applies only when both bounds are declared; without them the field stays the box it was. A
 * presentation that cannot be drawn degrades to the one that can, which is the rule the picklist's
 * presentations follow for the same reason.
 */
import { CaosBaseField } from './base-field.js';
import { define } from '../base-element.js';

export class CaosNumberField extends CaosBaseField {
  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, 'presentation'];
  }

  /** The chosen value, drawn beside the range — a range shows a position, never a number. */
  private readout: HTMLSpanElement;

  constructor() {
    super('number');
    this.readout = document.createElement('span');
    this.readout.className = 'readout';
    this.readout.setAttribute('part', 'readout');
    // A range control has no readable value of its own, so the readout IS the value for anyone
    // reading the field rather than looking at it. It is announced as the control's description
    // rather than hidden, which is what `aria-hidden` here would have made it.
    this.readout.id = `caos-readout-${this.input.id}`;
    this.row.append(this.readout);
  }

  // The `numeric` marker (the shared sheet keys tabular-nums off it) is set on connect,
  // NOT in the constructor: a custom-element constructor must not add attributes, or
  // `document.createElement('caos-number-field')` throws NotSupportedError (which the
  // imperative creation in caos-field-renderer hit).
  override connectedCallback(): void {
    if (!this.hasAttribute('numeric')) this.setAttribute('numeric', '');
    super.connectedCallback();
  }

  /**
   * True when this field is drawn as a range: asked for AND drawable. Both bounds have to be
   * declared, because a range without them spans a span nobody declared.
   */
  get slider(): boolean {
    return (
      this.getAttribute('presentation') === 'slider' &&
      this.getAttribute('min') !== null &&
      this.getAttribute('max') !== null
    );
  }

  protected override sync(): void {
    const slider = this.slider;
    // `range` and `number` are both inputs, so this is a type change rather than a control swap:
    // the same node, the same listeners, a different affordance over the same value.
    this.setInputType(slider ? 'range' : 'number');
    super.sync();
    // AFTER the shared pass, because the readout reports what the control now holds. Reading it
    // beforehand would draw the previous value — right on the second render, wrong on the first.
    this.readout.textContent = slider ? this.input.value : '';
    if (slider) this.input.setAttribute('aria-describedby', this.readout.id);
    else this.input.removeAttribute('aria-describedby');
  }
}

define('caos-number-field', CaosNumberField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-number-field': CaosNumberField;
  }
}
